import { Component } from 'react';
import { observable, action, makeObservable } from 'mobx';
import { observer } from 'mobx-react';

//ensure it works with default html img properties
type ImageWrapperProps = {
  id?: string;
  src: string;
  className?: string;
  alt?: string;
  width?: number | null;  // Known image width for placeholder sizing
  height?: number | null; // Known image height for placeholder sizing
  onUseImageAttachment?: () => void;
}

function isTouchDevice() {
  return (('ontouchstart' in window) || (navigator.maxTouchPoints > 0));
}

@observer
export class ImageWrapper extends Component<ImageWrapperProps> {
  @observable isFullscreen = false;
  @observable isLoading = false;
  @observable imageError = false;
  @observable zoomScale = 1;
  @observable translateX = 0;
  @observable translateY = 0;

  private initialDistance = 0;
  private initialScale = 1;
  private imageRect: DOMRect | null = null;
  private isPinching = false;
  private isTransitioning = false;
  private transformOrigin = 'center center';

  @action
  openFullscreen = () => {
    this.isFullscreen = true;
    document.body.style.overflow = 'hidden';
  };

  @action
  closeFullscreen = () => {
    this.isFullscreen = false;
    this.zoomScale = 1;
    this.translateX = 0;
    this.translateY = 0;
    document.body.style.overflow = 'unset';
  };

  @action
  setLoading = (loading: boolean) => {
    this.isLoading = loading;
  };

  @action
  setImageError = (error: boolean) => {
    this.imageError = error;
  };

  @action
  setZoom = (scale: number, x: number = 0, y: number = 0) => {
    this.zoomScale = scale;
    this.translateX = x;
    this.translateY = y;
  };

  @action
  resetZoom = () => {
    this.isTransitioning = true;
    this.zoomScale = 1;
    this.translateX = 0;
    this.translateY = 0;
    // Reset transition flag after animation
    setTimeout(() => {
      this.isTransitioning = false;
    }, 300);
  };

  @action
  centerZoom = () => {
    this.isTransitioning = true;
    this.translateX = 0;
    this.translateY = 0;
    // Reset transition flag after animation
    setTimeout(() => {
      this.isTransitioning = false;
    }, 300);
  };

  handleImageLoad = () => {
    this.setLoading(false);
    this.setImageError(false);
  };

  handleImageError = () => {
    this.setLoading(false);
    this.setImageError(true);
  };

  handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      this.closeFullscreen();
    }
  };

  getTouchDistance = (touches: React.TouchList): number => {
    const touch1 = touches[0];
    const touch2 = touches[1];
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  getTouchCenter = (touches: React.TouchList): { x: number; y: number } => {
    const touch1 = touches[0];
    const touch2 = touches[1];
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2
    };
  };

  handleTouchStart = (e: React.TouchEvent<HTMLImageElement>) => {
    if (this.isTransitioning) return;
    if (e.touches.length === 2) {
      e.preventDefault();
      this.isPinching = true;
      this.initialDistance = this.getTouchDistance(e.touches);
      this.initialScale = this.zoomScale;
      
      this.imageRect = e.currentTarget.getBoundingClientRect();
      const touchCenter = this.getTouchCenter(e.touches);
      
      const originX = touchCenter.x - this.imageRect.left;
      const originY = touchCenter.y - this.imageRect.top;
      this.transformOrigin = `${originX}px ${originY}px`;
    }
  };

  handleTouchMove = (e: React.TouchEvent<HTMLImageElement>) => {
    if (this.isTransitioning) return;
    if (this.isPinching && e.touches.length === 2) {
      e.preventDefault();
      const currentDistance = this.getTouchDistance(e.touches);
      let scale = (currentDistance / this.initialDistance) * this.initialScale;
      scale = Math.max(1, Math.min(scale, 5));
      this.setZoom(scale);
    }
  };

  handleTouchEnd = () => {
    this.isPinching = false;
    this.resetZoom();
    setTimeout(() => {
      this.transformOrigin = 'center center';
    }, 300);
  };

  downloadImage = async () => {
    try {
      const response = await fetch(this.props.src);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `image-${Date.now()}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  constructor(props: ImageWrapperProps) {
    super(props);
    makeObservable(this);
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
    document.body.style.overflow = 'unset';
  }

  render() {
    const { src, className = '', alt = 'Image', id, width, height, onUseImageAttachment } = this.props;

    const thumbnailStyle: React.CSSProperties = {
      cursor: 'pointer',
      maxWidth: '100%',
      height: 'auto',
      borderRadius: '8px',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease'
    };

    // Use known dimensions for placeholder sizing to prevent layout shift
    if (width && height) {
      thumbnailStyle.aspectRatio = `${width} / ${height}`;
      thumbnailStyle.maxWidth = Math.min(width, 512) + 'px';
      thumbnailStyle.width = width;
      thumbnailStyle.height = height;
      // adjust width and height to fit within 300x300 while maintaining aspect ratio
      const aspectRatio = width / height;
      if (aspectRatio > 1) {
        thumbnailStyle.width = '300px';
        thumbnailStyle.height = `${300 / aspectRatio}px`;
      } else {
        thumbnailStyle.height = '300px';
        thumbnailStyle.width = `${300 * aspectRatio}px`;
      }
    }

    return (
      <>
        {/* Thumbnail Image */}
        <span className={`image-wrapper ${className}`}>
          <img
            id={id}
            src={src}
            alt={alt}
            loading='lazy'
            className="thumbnail-image"
            onClick={this.openFullscreen}
            onLoad={this.handleImageLoad}
            onError={this.handleImageError}
            style={thumbnailStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.02)';
              e.currentTarget.style.boxShadow = 'var(--shadow)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
          {onUseImageAttachment && (
            <button
              className="attachment-use-as-ref-btn"
              title="Use as reference image"
              onClick={(e) => { e.stopPropagation(); onUseImageAttachment(); }}
            >
              &#x2197;
            </button>
          )}
        </span>

        {/* Fullscreen Modal */}
        {this.isFullscreen && (
          <span 
            className="fullscreen-overlay"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                this.closeFullscreen();
              }
            }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
              zIndex: 2000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
              animation: 'fadeIn 0.3s ease'
            }}
          >
            {/* Controls Bar */}
            <span 
              className="fullscreen-controls"
              style={{
                position: 'fixed',
                top: '20px',
                right: '20px',
                display: 'flex',
                gap: '12px',
                zIndex: 2001
              }}
            >
              <button
                onClick={this.downloadImage}
                className="control-button"
                title="Download Image"
                style={{
                  background: 'var(--bg-secondary)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  fontSize: '18px',
                  width: '48px',
                  height: '48px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--hover-bg)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--bg-secondary)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                ⬇
              </button>
              <button
                onClick={this.closeFullscreen}
                className="control-button"
                title="Close (ESC)"
                style={{
                  background: 'var(--bg-secondary)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  fontSize: '20px',
                  width: '48px',
                  height: '48px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--danger-color)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--bg-secondary)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                ✕
              </button>
            </span>

            {/* Full Size Image */}
            <span 
              className="fullscreen-image-container"
              style={{
                maxWidth: '95vw',
                maxHeight: '90vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                touchAction: 'none'
              }}
            >
              <img
                src={src}
                alt={alt}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100vh',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                  animation: 'zoomIn 0.3s ease',
                  transform: `scale(${this.zoomScale})`,
                  transformOrigin: this.transformOrigin,
                  transition: this.isTransitioning ? 'transform 0.3s ease' : 'none'
                }}
                onClick={(e) => e.stopPropagation()}
                onTouchStart={this.handleTouchStart}
                onTouchMove={this.handleTouchMove}
                onTouchEnd={this.handleTouchEnd}
              />
            </span>

            {/* Mobile-friendly close area indicator */}
            <span 
              style={{
                position: 'fixed',
                bottom: '20px',
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'var(--text-secondary)',
                fontSize: '14px',
                opacity: 0.7,
                textAlign: 'center',
                pointerEvents: 'none'
              }}
            >
              {!isTouchDevice() ? `Tap outside image or press ESC to close` : `Tap outside image to close`}
            </span>
          </span>
        )}
      </>
    );
  }
}